import React from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField, Typography, Stack, Box, Grid, useTheme, IconButton, Collapse, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ItemWasteChecksheetData, SaveStatus, WasteLogReason, DEFAULT_WASTE_REASONS, POSITION_TO_QTY_FIELD, POSITION_TO_STATUS_FIELD, POSITION_TO_UOM_FIELD, LEGACY_REASON_FIELDS, LEGACY_REASON_STATUS_FIELDS } from '../interfaces/ItemWasteChecksheetData';
import { Category } from '../interfaces/Category';
import { WasteChecksheetDetailContext, WasteChecksheetDetailValues } from '../contexts/WasteChecksheetDetailContext';
import { useContext, useCallback, useMemo, useState } from 'react';
import QuantityInputWithUom from './QuantityInputWithUom';
import { formatConversions, calculateItemCost } from '../utils/uomConversionUtils';
interface WasteChecksheetTableProps {
  category: Category;
  originalCategoryIndex: number;
}

const WasteChecksheetTable = ({ category, originalCategoryIndex }: WasteChecksheetTableProps) => {

  const { t,
    isArchived,
    hasPermissionToWrite,
    isMobile,
    isEditMode,
    handleItemFocus,
    handleQuantityChange,
    handleUomChange,
    wasteLogReasons,
    isWasteLogUnitSupportEnabled,
    itemConversions,
    currencySymbol,
    itemsByCategory,
  } = useContext(WasteChecksheetDetailContext) as WasteChecksheetDetailValues;

  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);

  // Use configured waste reasons or fall back to defaults
  const activeReasons = useMemo(() => {
    return wasteLogReasons.length > 0 ? wasteLogReasons : DEFAULT_WASTE_REASONS;
  }, [wasteLogReasons]);

  const reasonFieldMappings = useMemo(() => {
    const positionMap = new Map<number, number>();
    const qtyFieldMap = new Map<number, keyof ItemWasteChecksheetData>();
    const statusFieldMap = new Map<number, keyof ItemWasteChecksheetData>();
    const uomFieldMap = new Map<number, keyof ItemWasteChecksheetData>();

    activeReasons.forEach((reason, index) => {
      positionMap.set(reason.id, index);
      const legacyQtyField = LEGACY_REASON_FIELDS[reason.name];
      const legacyStatusField = LEGACY_REASON_STATUS_FIELDS[reason.name];
      qtyFieldMap.set(reason.id, legacyQtyField ?? POSITION_TO_QTY_FIELD[index] ?? 'pastDueQty');
      statusFieldMap.set(reason.id, legacyStatusField ?? POSITION_TO_STATUS_FIELD[index] ?? 'pastDueQtySaveStatus');
      uomFieldMap.set(reason.id, POSITION_TO_UOM_FIELD[index] ?? 'pastDueQtyUom');
    });

    return { positionMap, qtyFieldMap, statusFieldMap, uomFieldMap };
  }, [activeReasons]);

  const columnWidths = useMemo(() => {
    const reasonCount = activeReasons.length;
    if (isWasteLogUnitSupportEnabled) {
      const reasonWidth = reasonCount > 0 ? Math.floor(42 / reasonCount) : 10;
      return {
        itemNumber: '10%',
        itemDescription: '16%',
        lowestUom: '10%',
        reason: `${reasonWidth}%`,
        total: '6%',
        totalCost: '6%',
      };
    }
    const reasonWidth = reasonCount > 0 ? Math.floor(46 / reasonCount) : 12;
    return {
      itemNumber: '12%',
      itemDescription: '22%',
      lowestUom: '8%',
      reason: `${reasonWidth}%`,
      total: '8%',
      totalCost: '0%',
    };
  }, [activeReasons, isWasteLogUnitSupportEnabled]);

  // Get value for a reason from item
  const getReasonValue = (item: ItemWasteChecksheetData, reason: WasteLogReason): number | null | undefined => {
    const fieldKey = reasonFieldMappings.qtyFieldMap.get(reason.id);
    return fieldKey ? (item[fieldKey] as number | null | undefined) : undefined;
  };

  // Get save status for a reason from item
  const getReasonSaveStatus = (item: ItemWasteChecksheetData, reason: WasteLogReason): SaveStatus => {
    const statusFieldKey = reasonFieldMappings.statusFieldMap.get(reason.id);
    return statusFieldKey ? ((item[statusFieldKey] as SaveStatus) ?? 'standard') : 'standard';
  };

  // Get the field key for handleQuantityChange
  const getReasonFieldKey = (reason: WasteLogReason): keyof ItemWasteChecksheetData => {
    return reasonFieldMappings.qtyFieldMap.get(reason.id) ?? 'pastDueQty';
  };

  // Get the UOM field key for a reason
  const getReasonUomFieldKey = (reason: WasteLogReason): keyof ItemWasteChecksheetData => {
    return reasonFieldMappings.uomFieldMap.get(reason.id) ?? 'pastDueQtyUom';
  };

  // Get the UOM value for a reason from item
  const getReasonUom = (item: ItemWasteChecksheetData, reason: WasteLogReason): string => {
    const uomFieldKey = reasonFieldMappings.uomFieldMap.get(reason.id);
    const uomValue = uomFieldKey ? (item[uomFieldKey] as string | undefined) : undefined;
    return uomValue ?? item.uom ?? '';
  };

  const calculateTotal = (itemWaste: ItemWasteChecksheetData) => {
    // Sum all active reason quantities
    return activeReasons.reduce((total, reason) => {
      const value = getReasonValue(itemWaste, reason);
      return total + (value ?? 0);
    }, 0);
  };

  const calculateTotalByUom = (item: ItemWasteChecksheetData): { uom: string; total: number }[] => {
    const totals = new Map<string, number>();
    activeReasons.forEach((reason) => {
      const qty = getReasonValue(item, reason) ?? 0;
      if (qty === 0) return;
      const uom = getReasonUom(item, reason);
      totals.set(uom, (totals.get(uom) ?? 0) + qty);
    });
    if (totals.size === 0) return [{ uom: item.uom ?? '', total: 0 }];
    return Array.from(totals.entries()).map(([uom, total]) => ({ uom, total }));
  };

  const calculateTotalCost = (item: ItemWasteChecksheetData): number => {
    if (!item.availableUoms || !item.costPrice) return 0;
    const reasonQuantities = activeReasons.map((reason) => ({
      qty: (getReasonValue(item, reason) ?? 0) as number,
      uom: getReasonUom(item, reason),
    }));
    return calculateItemCost(reasonQuantities, item.costPrice, item.uom ?? '', item.availableUoms);
  };

  // Resolve the original item index in the unfiltered category
  const getOriginalItemIndex = useCallback((item: ItemWasteChecksheetData): number => {
    const originalCategory = itemsByCategory[originalCategoryIndex];
    if (!originalCategory) return 0;
    const idx = originalCategory.items.findIndex(i => i.id === item.id);
    return idx >= 0 ? idx : 0;
  }, [itemsByCategory, originalCategoryIndex]);

  const onQuantityChange = useCallback((catIdx: number, item: ItemWasteChecksheetData, field: keyof ItemWasteChecksheetData, value: string) => {
    handleQuantityChange(catIdx, getOriginalItemIndex(item), field, value);
  }, [handleQuantityChange, getOriginalItemIndex]);

  const onUomChangeHandler = useCallback((catIdx: number, item: ItemWasteChecksheetData, field: keyof ItemWasteChecksheetData, uom: string) => {
    handleUomChange(catIdx, getOriginalItemIndex(item), field, uom);
  }, [handleUomChange, getOriginalItemIndex]);

  // Pre-compute UOM conversion strings per item to avoid recalculating on every render
  const itemConversionStrings = useMemo(() => {
    if (!isWasteLogUnitSupportEnabled) return {};
    const strings: Record<string, string> = {};
    for (const item of category.items) {
      const key = item.itemNumber ?? '';
      strings[key] = formatConversions(item.uom ?? '', itemConversions[key]) || item.uom || '';
    }
    return strings;
  }, [category.items, itemConversions, isWasteLogUnitSupportEnabled]);

  if (isMobile) {
    return (
      <Box sx={{
        mb: 6,
        borderBottom: '1px solid',
        borderBottomColor: theme.palette.custom.gray[300],
      }}>
        <Box
          onClick={isWasteLogUnitSupportEnabled ? () => setExpanded(prev => !prev) : undefined}
          sx={{
            color: theme.palette.custom.gray[600],
            fontWeight: 600,
            borderBottom: '1px solid',
            borderBottomColor: theme.palette.custom.gray[300],
            borderTop: '1px solid',
            borderTopColor: theme.palette.custom.gray[300],
            backgroundColor: theme.palette.custom.gray[200],
            paddingTop: '8px',
            paddingBottom: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isWasteLogUnitSupportEnabled ? 'pointer' : undefined,
            userSelect: isWasteLogUnitSupportEnabled ? 'none' : undefined,
          }}>
          <Typography variant="textMD"
            fontWeight={600}>{category.name}</Typography>
          {isWasteLogUnitSupportEnabled && (
            <IconButton size="small"
              sx={{ color: theme.palette.custom.gray[600] }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        <Collapse in={expanded}>
          {category.items.map((item, itemIndex) => (
            <Box key={item.id}
              onFocus={isEditMode ? () => handleItemFocus(item.id ?? 0) : undefined}
              sx={{
                p: 4,
                borderBottom: itemIndex === category.items.length - 1 ? 'none' : '1px solid',
                borderBottomColor: theme.palette.custom.gray[300],
                ...(isWasteLogUnitSupportEnabled && item.isBom && {
                  backgroundColor: theme.palette.info[50],
                }),
              }}>
              <Grid container
                spacing={4}>
                <Grid item
                  xs={9}>
                  <Stack>
                    <Typography variant="textMD"
                      sx={{
                        color: theme.palette.custom.gray[600],
                        wordBreak: 'break-word',  // Ensures wrapping between words
                        overflowWrap: 'break-word', // Alternative for better compatibility
                        whiteSpace: 'normal', // Ensures text wraps instead of staying in a single line 
                      }}
                      fontWeight={600}> {item.description}</Typography>
                    <Typography variant="textXS"
                      sx={{ color: theme.palette.custom.gray[600] }}>{t('details.itemNumber')}: {item.itemNumber}</Typography>
                    {isWasteLogUnitSupportEnabled && item.isBom && (
                      <Chip
                        label="Recipe"
                        size="small"
                        sx={{
                          mt: 1,
                          alignSelf: 'flex-start',
                          backgroundColor: theme.palette.info[100],
                          color: theme.palette.info[700],
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    )}
                    {isWasteLogUnitSupportEnabled ? (
                      <Typography variant="textXS"
                        sx={{ color: theme.palette.custom.gray[600] }}>
                        {t('details.uomConversions')}: {itemConversionStrings[item.itemNumber ?? '']}
                      </Typography>
                    ) : (
                      <Typography variant="textXS"
                        sx={{ color: theme.palette.custom.gray[600] }}>{t('details.lowestUom')}: {item.uom}</Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid item
                  xs={3}
                  sx={{ display: 'flex', alignItems: 'center' }}>
                  {isWasteLogUnitSupportEnabled ? (
                    <Box>
                      <Typography variant="textXS"
                        fontWeight={600}
                        sx={{ color: theme.palette.custom.gray[600] }}>
                        {t('details.totalQty')}
                      </Typography>
                      <Typography variant="textXS"
                        sx={{ whiteSpace: 'pre-line', color: theme.palette.custom.gray[600] }}>
                        {calculateTotalByUom(item).map(({ uom, total }) => `${total} ${uom}`).join('\n')}
                      </Typography>
                    </Box>
                  ) : (
                    <TextField label={t('details.total')}
                      sx={{ minWidth: '60px' }}
                      type="number"
                      size="small"
                      id='total'
                      variant="outlined"
                      disabled
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { sx: { textAlign: 'right', pr: 2 } },
                      }}
                      value={calculateTotal(item)} />
                  )}
                </Grid>
                {isWasteLogUnitSupportEnabled && (
                  <Grid item
                    xs={12}>
                    <Typography variant="textXS"
                      sx={{ color: theme.palette.custom.gray[600] }}>
                      {t('details.totalCost')}: {currencySymbol}{calculateTotalCost(item).toFixed(2)}
                    </Typography>
                  </Grid>
                )}
                {activeReasons.map((reason) => (
                  <Grid item
                    xs={isWasteLogUnitSupportEnabled ? 6 : 3}
                    key={reason.id}
                    style={{ marginTop: 8 }}>
                    <QuantityInputWithUom label={reason.name}
                      setWidth={false}
                      id={`reason-${reason.id}`}
                      value={getReasonValue(item, reason) ?? ''}
                      disabled={isArchived || !hasPermissionToWrite}
                      onChange={(event) => onQuantityChange(originalCategoryIndex, item, getReasonFieldKey(reason), event.target.value)}
                      status={getReasonSaveStatus(item, reason)}
                      showUomSelect={isWasteLogUnitSupportEnabled}
                      uom={getReasonUom(item, reason)}
                      availableUoms={item.availableUoms}
                      onUomChange={(event) => onUomChangeHandler(originalCategoryIndex, item, getReasonUomFieldKey(reason), event.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
          }
        </Collapse>
      </Box >
    );
  }

  return (
    <Box sx={{ borderTop: '1px solid', borderBottom: '1px solid', borderColor: theme.palette.custom.gray[300], mb: 6 }}>
      <Box
        onClick={isWasteLogUnitSupportEnabled ? () => setExpanded(prev => !prev) : undefined}
        sx={{
          backgroundColor: theme.palette.custom.gray[200],
          mb: expanded ? 2 : 0,
          p: 4,
          fontWeight: 500,
          borderBottom: '1px solid',
          borderColor: theme.palette.custom.gray[300],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isWasteLogUnitSupportEnabled ? 'pointer' : undefined,
          userSelect: isWasteLogUnitSupportEnabled ? 'none' : undefined,
        }}
        className="title">
        {category.name}
        {isWasteLogUnitSupportEnabled && (
          <IconButton size="small"
            sx={{ color: theme.palette.custom.gray[600] }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      <Collapse in={expanded}>
        <TableContainer id='waste-checksheet-items-table-container'
          sx={{ maxWidth: '100%', borderTop: '1px solid', borderColor: theme.palette.custom.gray[300] }}
          className='waste-checksheet-categories'
          key={category.name}>
          <Table>
            <TableHead>
              <TableRow className='normal-field'>
                <TableCell
                  align="left"
                  style={{ width: columnWidths.itemNumber }}>
                  {t('details.itemNumber')}
                </TableCell>
                <TableCell
                  align="left"
                  style={{ width: columnWidths.itemDescription }}>
                  {t('details.itemDescription')}
                </TableCell>
                <TableCell
                  align="left"
                  style={{ width: columnWidths.lowestUom }}>
                  {isWasteLogUnitSupportEnabled ? t('details.uomConversions') : t('details.lowestUom')}
                </TableCell>
                {activeReasons.map((reason) => (
                  <TableCell
                    key={reason.id}
                    align="left"
                    style={{ width: columnWidths.reason }}>
                    {reason.name}
                  </TableCell>
                ))}
                <TableCell
                  align="left"
                  style={{ width: columnWidths.total }}>
                  {isWasteLogUnitSupportEnabled ? t('details.totalQty') : t('details.total')}
                </TableCell>
                {isWasteLogUnitSupportEnabled && (
                  <TableCell
                    align="left"
                    style={{ width: columnWidths.totalCost }}>
                    {t('details.totalCost')}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {category.items.map((item) => (
                <TableRow key={item.id}
                  className='normal-field'
                  onFocus={isEditMode ? () => handleItemFocus(item.id ?? 0) : undefined}
                  sx={isWasteLogUnitSupportEnabled && item.isBom ? { backgroundColor: theme.palette.info[50] } : undefined}>
                  <TableCell className="title">{item.itemNumber}</TableCell>
                  <TableCell className="description"
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {item.description}
                      {isWasteLogUnitSupportEnabled && item.isBom && (
                        <Chip
                          label="Recipe"
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.info[100],
                            color: theme.palette.info[700],
                            fontSize: '0.7rem',
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell className="uom">
                    {isWasteLogUnitSupportEnabled ? (
                      <Typography variant="textXS"
                        sx={{ whiteSpace: 'pre-line' }}>
                        {itemConversionStrings[item.itemNumber ?? '']}
                      </Typography>
                    ) : item.uom}
                  </TableCell>
                  {activeReasons.map((reason) => (
                    <TableCell key={reason.id}>
                      <QuantityInputWithUom
                        label={null}
                        setWidth={true}
                        id={`reason-${reason.id}`}
                        value={getReasonValue(item, reason) ?? ''}
                        disabled={isArchived || !hasPermissionToWrite}
                        onChange={(event) => onQuantityChange(originalCategoryIndex, item, getReasonFieldKey(reason), event.target.value)}
                        status={getReasonSaveStatus(item, reason)}
                        showUomSelect={isWasteLogUnitSupportEnabled}
                        uom={getReasonUom(item, reason)}
                        availableUoms={item.availableUoms}
                        onUomChange={(event) => onUomChangeHandler(originalCategoryIndex, item, getReasonUomFieldKey(reason), event.target.value)}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    {isWasteLogUnitSupportEnabled ? (
                      <Typography variant="textSM"
                        sx={{ whiteSpace: 'pre-line' }}>
                        {calculateTotalByUom(item).map(({ uom, total }) => `${total} ${uom}`).join('\n')}
                      </Typography>
                    ) : (
                      <TextField sx={{ minWidth: '60px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                        type="number"
                        size="small"
                        id='total'
                        variant="outlined"
                        disabled
                        InputProps={{
                          inputProps: { sx: { textAlign: 'right', pr: 2 } },
                        }}
                        value={calculateTotal(item)} />
                    )}
                  </TableCell>
                  {isWasteLogUnitSupportEnabled && (
                    <TableCell>
                      <Typography variant="textSM">
                        {currencySymbol}{calculateTotalCost(item).toFixed(2)}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer >
      </Collapse>
    </Box >
  );
};

export default React.memo(WasteChecksheetTable);
